import { onUnmounted, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { ReportEvents, type GcomeKeyDebugUpload } from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'

/** GCOME_AP 按键校准：管理校准状态、成功按键与自适应校准开关。 */
export function useCalibration() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const isCalibrating = shallowRef(false)
  const calibrationPending = shallowRef(false)
  const adaptiveCalibrationEnabled = shallowRef(false)
  const calibrationCounts = shallowRef(new Map<string, number>())
  let cleanup: (() => void) | null = null
  let disposed = false

  const getKeyTravelFeature = () => currentDevice.value?.features.keyTravel

  const onCalibrationUpload = (data: GcomeKeyDebugUpload) => {
    if (data.keyInfo[2] === 7) console.log(data)
    if (!isCalibrating.value) return
    calibrationCounts.value = new Map(calibrationCounts.value).set(
      data.keyInfo.join('.'),
      data.calibCount,
    )
  }

  const bindUploadListener = () => {
    cleanup?.()
    const device = currentDevice.value
    if (!device) return

    device.on(ReportEvents.KeyCalibUpload, onCalibrationUpload)
    cleanup = () => device.off(ReportEvents.KeyCalibUpload, onCalibrationUpload)
  }

  const loadAdaptiveCalibration = async () => {
    const device = currentDevice.value
    const perfFeature = device?.features.keyboardPerfCfg
    if (!device || !perfFeature) return

    try {
      const enabled = await perfFeature.getAdaptiveCalibration()
      if (currentDevice.value === device) adaptiveCalibrationEnabled.value = enabled
    } catch {
      // 自适应校准读取失败时保留当前界面状态，避免影响其他校准功能。
    }
  }

  const reportCalibrationError = (message: string, error?: unknown) => {
    console.error(message, error)
    defaultReportError(error ?? message)
  }

  async function startCalibration() {
    if (calibrationPending.value) return

    const keyTravel = getKeyTravelFeature()
    if (!keyTravel) {
      reportCalibrationError('Current device does not support key travel')
      return
    }

    calibrationPending.value = true
    try {
      calibrationCounts.value = new Map()
      await keyTravel.switchDemoMode(true)
      isCalibrating.value = true
      await keyTravel.switchCalibrationMode(true)
      if (disposed) {
        isCalibrating.value = false
        calibrationCounts.value = new Map()
        await keyTravel.switchCalibrationMode(false).catch(() => undefined)
        return
      }
    } catch (error) {
      isCalibrating.value = false
      calibrationCounts.value = new Map()
      reportCalibrationError('Failed to start calibration', error)
    } finally {
      calibrationPending.value = false
    }
  }

  async function finishCalibration() {
    if (calibrationPending.value || !isCalibrating.value) return

    const keyTravel = getKeyTravelFeature()
    if (!keyTravel) {
      reportCalibrationError('Current device does not support key travel')
      return
    }

    calibrationPending.value = true
    try {
      await keyTravel.saveCalibration('SMD')
      isCalibrating.value = false
      calibrationCounts.value = new Map()
    } catch (error) {
      reportCalibrationError('Failed to save calibration', error)
    } finally {
      calibrationPending.value = false
    }
  }

  async function cancelCalibration() {
    if (!isCalibrating.value || calibrationPending.value) {
      calibrationCounts.value = new Map()
      return
    }

    const keyTravel = getKeyTravelFeature()
    if (!keyTravel) return

    calibrationPending.value = true
    try {
      await keyTravel.switchCalibrationMode(false)
      isCalibrating.value = false
      calibrationCounts.value = new Map()
    } catch (error) {
      reportCalibrationError('Failed to cancel calibration', error)
    } finally {
      calibrationPending.value = false
    }
  }

  async function setAdaptiveCalibration(enabled: boolean) {
    if (calibrationPending.value) return

    const keyTravel = getKeyTravelFeature()
    if (!keyTravel) {
      reportCalibrationError('Current device does not support key travel')
      return
    }

    calibrationPending.value = true
    try {
      await keyTravel.switchAdaptiveCalibration(enabled)
      adaptiveCalibrationEnabled.value = enabled
    } catch (error) {
      reportCalibrationError('Failed to set adaptive calibration', error)
    } finally {
      calibrationPending.value = false
    }
  }

  watch(
    currentDevice,
    () => {
      calibrationCounts.value = new Map()
      bindUploadListener()
      void loadAdaptiveCalibration()
    },
    { immediate: true },
  )

  onUnmounted(() => {
    disposed = true
    cleanup?.()
    cleanup = null
    if (isCalibrating.value) void cancelCalibration()
  })

  return {
    isCalibrating,
    calibrationPending,
    adaptiveCalibrationEnabled,
    calibrationCounts,
    startCalibration,
    finishCalibration,
    cancelCalibration,
    setAdaptiveCalibration,
  }
}
